import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { ArrowLeft } from "lucide-react";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import JerseyNumber from "../utils/JerseyNumber";

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    finished: {
      label: "Final",
      cls: "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
    },
    live: {
      label: "Live",
      cls: "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400",
    },
    scheduled: {
      label: "Scheduled",
      cls: "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400",
    },
  };
  const { label, cls } = map[status] ?? map.scheduled;
  return (
    <span
      className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${cls}`}
    >
      {label}
    </span>
  );
};

// ── Totals row computation ────────────────────────────────────────────────────
const sumCol = (rows, key) => rows.reduce((s, r) => s + (r[key] ?? 0), 0);

// ── Team tab button ───────────────────────────────────────────────────────────
const TabBtn = ({ active, color, label, onClick }) => {
  const textColor = active
    ? getTextColorForAccent(color, isDarkModeActive())
    : undefined;
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
        active
          ? "shadow-sm"
          : "text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700"
      }`}
      style={active ? { backgroundColor: color, color: textColor } : undefined}
    >
      {label}
    </button>
  );
};

// ── Shooting line (FG / 3PT / FT) ────────────────────────────────────────────
// two_pm + three_pm = total field goals made (we don't store attempts)
const ShootingLine = ({ rows }) => {
  const fg = sumCol(rows, "two_pm") + sumCol(rows, "three_pm");
  const tpm = sumCol(rows, "three_pm");
  const ft = sumCol(rows, "ft_made");
  return (
    <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
      <span>
        FGM <strong className="text-gray-700 dark:text-gray-200">{fg}</strong>
      </span>
      <span>
        3PM <strong className="text-gray-700 dark:text-gray-200">{tpm}</strong>
      </span>
      <span>
        FTM <strong className="text-gray-700 dark:text-gray-200">{ft}</strong>
      </span>
    </div>
  );
};

// ── Helper: find the max value for a stat key across rows ────────────────────
const maxCol = (rows, key) =>
  rows.reduce((m, r) => Math.max(m, r[key] ?? 0), 0);

// ── Helper: perceived brightness of a hex color (0–255) ──────────────────────
const brightness = (hex) => {
  const c = (hex || "#000000").replace("#", "").padEnd(6, "0");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
};

/**
 * Resolve a background tint for the highlighted stat cell.
 *
 * Strategy:
 *  1. If secondaryHex is "usable" (not near-white or near-black, i.e. brightness
 *     between 30 and 210), use it at low opacity as the tint.
 *  2. Otherwise fall back to primaryHex at low opacity.
 *
 * The opacity is kept low (12–18%) so the tint is subtle but visible on both
 * light and dark backgrounds.
 */
const resolveHighlightBg = (primaryHex, secondaryHex) => {
  const secB = brightness(secondaryHex);
  const usableSecondary = secB > 30 && secB < 210;
  const tintColor = usableSecondary ? secondaryHex : primaryHex;
  // Use a slightly higher opacity in light mode (where the page bg is white)
  // so the tint is perceptible; dark mode already has contrast from the dark bg.
  return `${tintColor}22`; // ~13% opacity — works on both light & dark
};

// ── Stat cell — right-aligned, dimmed zeros, optional accent highlight ────────
const StatCellHighlight = ({ value, isTop, primaryHex, secondaryHex }) => {
  const val = value ?? 0;
  const isZero = val === 0;

  let cellStyle;
  if (isTop && !isZero) {
    cellStyle = {
      color: primaryHex,
      backgroundColor: resolveHighlightBg(primaryHex, secondaryHex),
    };
  }

  return (
    <td
      className={`px-3 py-3 text-right text-sm tabular-nums font-bold
        ${isZero ? "text-gray-300 dark:text-gray-600" : !isTop ? "text-gray-700 dark:text-gray-200" : ""}`}
      style={cellStyle}
    >
      {val}
    </td>
  );
};

// ── Player stats table for one team ──────────────────────────────────────────
const TeamTable = ({ rows, accentHex, primaryHex, secondaryHex }) => {
  if (!rows.length) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
        No stats recorded.
      </p>
    );
  }

  const sorted = [...rows].sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
  const accent = accentHex || "#6b7280";
  const primary = primaryHex || accentHex || "#6b7280";
  const secondary = secondaryHex || accentHex || "#6b7280";

  // Compute per-column maximums (only highlight if > 0)
  const maxPts = maxCol(sorted, "points");
  const maxReb = maxCol(sorted, "rebounds");
  const maxAst = maxCol(sorted, "assists");
  const max3pm = maxCol(sorted, "three_pm");
  const maxFtm = maxCol(sorted, "ft_made");

  // Pick a jersey number color that contrasts against the page background
  const isDark = isDarkModeActive();
  const pageBg = isDark ? "#1f2937" : "#ffffff"; // bg-gray-800 / white
  const jerseyColor = (() => {
    const hexBrightness = (hex) => {
      const c = hex.replace("#", "");
      const r = parseInt(c.slice(0, 2), 16);
      const g = parseInt(c.slice(2, 4), 16);
      const b = parseInt(c.slice(4, 6), 16);
      return r * 0.2126 + g * 0.7152 + b * 0.0722;
    };
    const pageBrightness = hexBrightness(pageBg);
    // Try accent first, then primary; pick whichever contrasts more
    const accentContrast = Math.abs(hexBrightness(accent) - pageBrightness);
    const primaryContrast = Math.abs(hexBrightness(primary) - pageBrightness);
    if (accentContrast >= 60) return accent;
    if (primaryContrast >= 60) return primary;
    // Neither contrasts enough — fall back to a safe readable color
    return isDark ? "#e5e7eb" : "#374151";
  })();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr
            className="text-[11px] uppercase tracking-wider
                                   text-gray-400 dark:text-gray-500 border-b
                                   border-gray-100 dark:border-gray-700"
          >
            <th className="text-left px-5 py-3 font-medium">Player</th>
            <th className="text-right px-3 py-3 font-medium">PTS</th>
            <th className="text-right px-3 py-3 font-medium">REB</th>
            <th className="text-right px-3 py-3 font-medium">AST</th>
            <th className="text-right px-3 py-3 font-medium">3PM</th>
            <th className="text-right px-5 py-3 font-medium">FTM</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
          {sorted.map((row) => (
            <tr
              key={row.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors"
            >
              <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">
                <div className="flex items-center gap-2">
                  {row.jersey_number != null && (
                    <JerseyNumber
                      number={row.jersey_number}
                      color={jerseyColor}
                      size="xs"
                      mode="badge"
                    />
                  )}
                  {row.player_name}
                </div>
              </td>
              <StatCellHighlight
                value={row.points}
                isTop={(row.points ?? 0) === maxPts && maxPts > 0}
                primaryHex={primary}
                secondaryHex={secondary}
              />
              <StatCellHighlight
                value={row.rebounds}
                isTop={(row.rebounds ?? 0) === maxReb && maxReb > 0}
                primaryHex={primary}
                secondaryHex={secondary}
              />
              <StatCellHighlight
                value={row.assists}
                isTop={(row.assists ?? 0) === maxAst && maxAst > 0}
                primaryHex={primary}
                secondaryHex={secondary}
              />
              <StatCellHighlight
                value={row.three_pm}
                isTop={(row.three_pm ?? 0) === max3pm && max3pm > 0}
                primaryHex={primary}
                secondaryHex={secondary}
              />
              <StatCellHighlight
                value={row.ft_made}
                isTop={(row.ft_made ?? 0) === maxFtm && maxFtm > 0}
                primaryHex={primary}
                secondaryHex={secondary}
              />
            </tr>
          ))}
        </tbody>

        {/* Totals row */}
        <tfoot>
          <tr
            className="border-t-2 border-gray-200 dark:border-gray-600
                                   text-[11px] uppercase tracking-wider
                                   text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30"
          >
            <td className="px-5 py-2.5 font-semibold">Team total</td>
            <td className="px-3 py-2.5 text-right font-bold text-gray-900 dark:text-white tabular-nums">
              {sumCol(sorted, "points")}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {sumCol(sorted, "rebounds")}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {sumCol(sorted, "assists")}
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">
              {sumCol(sorted, "three_pm")}
            </td>
            <td className="px-5 py-2.5 text-right tabular-nums">
              {sumCol(sorted, "ft_made")}
            </td>
          </tr>
        </tfoot>
      </table>

      <div className="px-5 py-3 border-t border-gray-50 dark:border-gray-700/40">
        <ShootingLine rows={sorted} />
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const GameBoxScore = () => {
  const { id } = useParams();
  const gameId = parseInt(id, 10);
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [statRows, setStatRows] = useState([]); // flat list, grouped client-side
  const [activeTab, setActiveTab] = useState(null); // team id or 'all'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGame = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Game details
      const { data: gameData, error: gameErr } = await supabase
        .from("games")
        .select(
          `
        id, game_date, status, court, game_type,
        team1_id, team2_id, team1_score, team2_score, scorer_id,
        team1:teams!games_team1_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient),
        team2:teams!games_team2_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)
    `,
        )
        .eq("id", gameId)
        .single();
      if (gameErr) throw gameErr;

      let scorerName = null;
      if (gameData.scorer_id) {
        const { data: scorerRow } = await supabase
          .from("public_profile_names")
          .select("name")
          .eq("id", gameData.scorer_id)
          .maybeSingle();
        scorerName = scorerRow?.name ?? null;
      }

      setGame({
        ...gameData,
        scorer: scorerName ? { name: scorerName } : null,
      });
      setActiveTab(gameData.team1_id);

      // 2. All player stats for this game
      //    Join players to get name + team_id for grouping
      const { data: stats, error: statsErr } = await supabase
        .from("game_stats")
        .select(
          `
                    id, points, rebounds, assists,
                    three_pm, two_pm, ft_made,
                    personal_fouls, technical_fouls,
                    player:players!game_stats_player_id_fkey (
                        id, name, team_id, jersey_number
                    )
                `,
        )
        .eq("game_id", gameId);
      if (statsErr) throw statsErr;

      // Flatten player fields up for easier use
      const flat = (stats || []).map((s) => ({
        ...s,
        player_name: s.player?.name ?? "Unknown",
        player_team_id: s.player?.team_id,
        jersey_number: s.player?.jersey_number ?? null,
      }));
      setStatRows(flat);
    } catch (err) {
      console.error("GameBoxScore fetch error:", err);
      setError("Failed to load game data.");
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const team1Rows = statRows.filter((r) => r.player_team_id === game?.team1_id);
  const team2Rows = statRows.filter((r) => r.player_team_id === game?.team2_id);

  const visibleRows =
    activeTab === "all"
      ? statRows
      : activeTab === game?.team1_id
        ? team1Rows
        : team2Rows;

  // Determine winner
  const isFinal = game?.status === "finished";
  const team1Wins = isFinal && game.team1_score > game.team2_score;
  const team2Wins = isFinal && game.team2_score > game.team1_score;

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-gray-400 dark:text-gray-500 text-sm animate-pulse">
          Loading box score...
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="text-center py-24">
        <p className="text-red-500 dark:text-red-400 text-sm mb-4">
          {error ?? "Game not found."}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-orange-500 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const gameTypeLabel =
    game.game_type === "playoff"
      ? "Playoffs"
      : game.game_type === "scrimmage"
        ? "Scrimmage"
        : "Regular Season";

  return (
    <div className="space-y-5">
      {/* ── Back link ── */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400
                           hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <ArrowLeft size={15} />
        Back
      </button>

      {/* ── Score header — split team colors ── */}
      <div className="rounded-2xl overflow-hidden shadow-sm">
        {/* Meta strip — neutral dark bar above the color split */}
        <div className="flex items-center justify-center gap-3 py-2.5 px-6 bg-gray-100 dark:bg-gray-900">
          <StatusBadge status={game.status} />
          <span className="text-xs text-gray-500">
            {new Date(game.game_date).toLocaleDateString("en-US", {
              weekday: "short",
              month: "long",
              day: "numeric",
            })}
          </span>
          {game.court && (
            <span className="text-xs text-gray-500">· {game.court}</span>
          )}
          <span className="text-xs text-gray-500">· {gameTypeLabel}</span>
          {game.scorer?.name && (
            <span className="text-xs text-gray-500">
              · Scored by{" "}
              <span className="text-gray-400 font-medium">
                {game.scorer.name}
              </span>
            </span>
          )}
        </div>

        {/* Split color panel */}
        <div className="flex items-stretch">
          {/* Team 1 side */}
          {(() => {
            const ts1 = getTeamStyle(game.team1);
            const isDark = isDarkModeActive();
            const t1Color = getTextColorForAccent(ts1.primaryHex, isDark);
            const t1IsLight = t1Color === "#ffffff";
            const t1Sub = t1IsLight
              ? "rgba(255,255,255,0.25)"
              : "rgba(0,0,0,0.12)";
            const t1Badge = t1IsLight
              ? "rgba(255,255,255,0.20)"
              : "rgba(0,0,0,0.10)";
            return (
              <div
                className="flex-1 flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-l-2xl"
                style={ts1.headerStyle}
              >
                {game.team1?.logo_url && (
                  <img
                    src={game.team1.logo_url}
                    className="w-14 h-14 rounded-full border-2 shadow-sm object-contain"
                    style={{ borderColor: t1Sub, backgroundColor: t1Badge }}
                    alt=""
                  />
                )}
                <button
                  onClick={() => navigate(`/teams/${game.team1_id}`)}
                  className="text-sm font-bold transition-colors text-center leading-tight"
                  style={{ color: t1Color, opacity: 0.9 }}
                >
                  {game.team1?.name}
                </button>
                {team1Wins && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest"
                    style={{ color: t1Color, backgroundColor: t1Badge }}
                  >
                    Winner
                  </span>
                )}
                <span
                  className="text-5xl font-black tabular-nums leading-none mt-1"
                  style={{ color: t1Color, opacity: team2Wins ? 0.5 : 1 }}
                >
                  {game.team1_score ?? 0}
                </span>
              </div>
            );
          })()}

          {/* Team 2 side */}
          {(() => {
            const ts2 = getTeamStyle(game.team2);
            const isDark = isDarkModeActive();
            const t2Color = getTextColorForAccent(ts2.primaryHex, isDark);
            const t2IsLight = t2Color === "#ffffff";
            const t2Sub = t2IsLight
              ? "rgba(255,255,255,0.25)"
              : "rgba(0,0,0,0.12)";
            const t2Badge = t2IsLight
              ? "rgba(255,255,255,0.20)"
              : "rgba(0,0,0,0.10)";
            return (
              <div
                className="flex-1 flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-r-2xl"
                style={ts2.headerStyle}
              >
                {game.team2?.logo_url && (
                  <img
                    src={game.team2.logo_url}
                    className="w-14 h-14 rounded-full border-2 shadow-sm object-contain"
                    style={{ borderColor: t2Sub, backgroundColor: t2Badge }}
                    alt=""
                  />
                )}
                <button
                  onClick={() => navigate(`/teams/${game.team2_id}`)}
                  className="text-sm font-bold transition-colors text-center leading-tight"
                  style={{ color: t2Color, opacity: 0.9 }}
                >
                  {game.team2?.name}
                </button>
                {team2Wins && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest"
                    style={{ color: t2Color, backgroundColor: t2Badge }}
                  >
                    Winner
                  </span>
                )}
                <span
                  className="text-5xl font-black tabular-nums leading-none mt-1"
                  style={{ color: t2Color, opacity: team1Wins ? 0.5 : 1 }}
                >
                  {game.team2_score ?? 0}
                </span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Tab switcher + stats table ── */}
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm
                            border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        {/* Tabs */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <TabBtn
            active={activeTab === game.team1_id}
            color={getTeamStyle(game.team1).primaryHex}
            label={game.team1?.name ?? "Team 1"}
            onClick={() => setActiveTab(game.team1_id)}
          />
          <TabBtn
            active={activeTab === game.team2_id}
            color={getTeamStyle(game.team2).primaryHex}
            label={game.team2?.name ?? "Team 2"}
            onClick={() => setActiveTab(game.team2_id)}
          />
          <TabBtn
            active={activeTab === "all"}
            color="#6b7280"
            label="All"
            onClick={() => setActiveTab("all")}
          />
        </div>

        {/* Table */}
        {statRows.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10">
            {game.status === "scheduled"
              ? "This game hasn't been played yet."
              : "No stats recorded for this game."}
          </p>
        ) : (
          <TeamTable
            rows={visibleRows}
            accentHex={
              activeTab === game.team1_id
                ? getTeamStyle(game.team1).accentHex
                : activeTab === game.team2_id
                  ? getTeamStyle(game.team2).accentHex
                  : "#6b7280"
            }
            primaryHex={
              activeTab === game.team1_id
                ? getTeamStyle(game.team1).primaryHex
                : activeTab === game.team2_id
                  ? getTeamStyle(game.team2).primaryHex
                  : "#6b7280"
            }
            secondaryHex={
              activeTab === game.team1_id
                ? getTeamStyle(game.team1).accentHex
                : activeTab === game.team2_id
                  ? getTeamStyle(game.team2).accentHex
                  : "#6b7280"
            }
          />
        )}
      </div>

      {/* ── Fouls footnote (if any recorded) ── */}
      {statRows.some(
        (r) => (r.personal_fouls ?? 0) > 0 || (r.technical_fouls ?? 0) > 0,
      ) && (
        <div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm
                                border border-gray-100 dark:border-gray-700 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200">
              Fouls
            </h3>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
            {statRows
              .filter(
                (r) =>
                  (r.personal_fouls ?? 0) > 0 || (r.technical_fouls ?? 0) > 0,
              )
              .sort((a, b) => (b.personal_fouls ?? 0) - (a.personal_fouls ?? 0))
              .map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 px-5 py-2.5 text-sm"
                >
                  <span className="flex-1 text-gray-700 dark:text-gray-200">
                    {r.player_name}
                  </span>
                  {(r.personal_fouls ?? 0) > 0 && (
                    <span className="text-xs text-gray-500">
                      {r.personal_fouls} PF
                    </span>
                  )}
                  {(r.technical_fouls ?? 0) > 0 && (
                    <span className="text-xs font-semibold text-red-500">
                      {r.technical_fouls} T
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoxScore;
